import { Text } from '@/components/commons/typography/Text'
import { TextSkeleton } from '@/components/skeleton/TextSkeleton'
import { ProfilesIcon } from '@/public/assets/v2/images/icons'
import { cssClass } from '@/shared/utils'
import {
  useMutualConnections,
  useMutualConnectionsCount,
} from 'containers/profile-network/hooks/useMutualConnections'
import {
  useIsOwnProfile,
  useProfile,
} from 'containers/profile/hooks/useProfile'
import { Avatar } from 'containers/recommendations/Avatar'
import { useUser } from 'containers/user/hooks/useUser'
import { User } from 'containers/user/types/user'
import Link from 'next/link'
import { useMemo } from 'react'
import { APP_LINKS, joinWithSlash } from 'utilities/links'

interface MutualConnectionsProps {
  showAvatars?: boolean
  showNames?: boolean
  profile?: User
  namesToShowCount?: number
  className?: string
}

export const MutualConnections = ({
  showAvatars = true,
  showNames = true,
  namesToShowCount = 2,
  profile,
  className = '',
}: MutualConnectionsProps) => {
  const { isAuth } = useUser()

  const { profile: currentProfile } = useProfile()
  const usingProfile = profile?.id ? profile : currentProfile
  const usingProfileId = usingProfile?.id
  const isOwnProfile = useIsOwnProfile(profile)

  const { count, isLoading } = useMutualConnectionsCount({
    connectionId: usingProfileId,
  })
  const { data, isLoading: loadingProfiles } = useMutualConnections({
    connectionId: usingProfileId,
    limit: namesToShowCount,
  })

  const mutualConnectionsNames = useMemo(() => {
    return data?.map((user) => `${user.firstName} ${user.lastName}`).join(', ')
  }, [data])

  const displayText = useMemo(() => {
    if (!showNames) {
      return `${count} mutual connections`
    }
    if (count > namesToShowCount) {
      return `, and ${count - namesToShowCount} other mutual connections`
    }
    if (count === 2) {
      return ` are mutual connections`
    }
    if (count === 1) {
      return ` is a mutual connection`
    }
  }, [count, showNames, namesToShowCount])

  // Return null if it's own profile or there are no mutual connections
  if (isOwnProfile || count === 0 || !isAuth) return null

  return (
    <Link
      href={joinWithSlash(
        usingProfile?.username?.toLocaleLowerCase(),
        APP_LINKS.PROFILE_NETWORK,
        APP_LINKS.PROFILE_CONNECTIONS,
        APP_LINKS.PROFILE_MUTUAL_CONNECTIONS
      )}
      className={cssClass([
        'items-center gap-4 hover__text-underline',
        'flex',
        className,
      ])}
    >
      {showAvatars && (
        <div className="flex relative">
          {isLoading || loadingProfiles ? (
            <>
              <Avatar size={32} image="" isLoading={true} />
              <Avatar
                size={32}
                image=""
                isLoading={true}
                containerClassName={cssClass(['relative -left-10 z-10'])}
              />
            </>
          ) : (
            data?.map((user, index) => (
              <Avatar
                key={user.id}
                size={32}
                image={user.avatar}
                isLoading={false}
                containerClassName={cssClass([
                  'relative',
                  index === 0
                    ? ''
                    : '-left-10 z-10 border-1 border-solid border-white box-content',
                ])}
              />
            ))
          )}
        </div>
      )}

      {(isLoading || loadingProfiles) && (
        <div className="flex gap-4">
          <TextSkeleton width={76} height={14} />
          <TextSkeleton width={50} height={14} />
        </div>
      )}

      {!showAvatars && !isLoading && !loadingProfiles && (
        <ProfilesIcon className="text-tertirary text-14 leading-20" />
      )}

      <Text variation="xSmall" className="break-text">
        {showNames && !isLoading && !loadingProfiles && (
          <Text variation="xSmall" as="span" weightClass="font-bold">
            {mutualConnectionsNames}
          </Text>
        )}
        {!isLoading && !loadingProfiles ? displayText : ''}
      </Text>
    </Link>
  )
}
